import { useState } from 'react'
import { Camera, FilePdf } from '@phosphor-icons/react'
import { useCan } from '@/auth/context'
import { istDateKey } from '@/lib/ist'
import { Banner, Button, Field, Input, Modal, Select, Spinner, useToast } from '@/ui'
import { errorMessage } from '../../api/core'
import {
  useApprovedLabels,
  useHallEquipment,
  usePackingHalls,
  useStartPackRun,
  type PackRunRow,
  type StartedRun,
} from '../../api/packFull'
import { uploadEvidence } from '../../api/reference'
import { openFile } from '../../lib/download'
import { Code, Facts, Money, RefLink, When } from '../bits'
import { PhotoViewer } from '../PhotoViewer'
import { RefusalBanner } from './Refusal'
import { refusalOf } from '../../model/pack/refusal'
import {
  emptyStartDraft,
  scaleOff,
  startBody,
  startOk,
  validateStartDraft,
  type StartDraft,
} from '../../model/pack/runForms'

/** The server's tolerance when a scale carries none (PACK_DEFAULTS.SCALE_CHECK_TOLERANCE_G). */
const DEFAULT_TOLERANCE_G = 2

/**
 * Start a planned pack run (F7.2) from the hall desk: cleanout with a photo of the empty hopper,
 * leftover from the last run, scale check, the label roll. The pack lot code, dates and QR link
 * are fixed by the server and shown here to key into the coder.
 */
export function StartRunDialog({
  run,
  netG,
  open,
  onClose,
}: {
  run: PackRunRow
  netG: number | null
  open: boolean
  onClose: () => void
}) {
  if (!open) return null
  return <StartRunForm run={run} netG={netG} onClose={onClose} />
}

function StartRunForm({
  run,
  netG,
  onClose,
}: {
  run: PackRunRow
  netG: number | null
  onClose: () => void
}) {
  const can = useCan()
  const toast = useToast()
  const [d, setD] = useState<StartDraft>(
    emptyStartDraft({ referenceG: netG ? String(netG) : '500' }),
  )
  const [tried, setTried] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [started, setStarted] = useState<StartedRun | null>(null)
  const set = (patch: Partial<StartDraft>) => setD((x) => ({ ...x, ...patch }))

  const halls = usePackingHalls()
  const hall = halls.data?.find((h) => h.id === run.locationId) ?? null
  const equipment = useHallEquipment(run.locationId)
  const scales = (equipment.data ?? []).filter((e) => e.isScale)
  const scale = scales.find((s) => s.id === d.scaleId) ?? null
  const labels = useApprovedLabels(run.skuId, run.locationId)
  const label = labels.data?.find((l) => l.id === d.labelVersionId) ?? null
  const start = useStartPackRun()

  const p = validateStartDraft(d)
  const shown = (k: keyof StartDraft) => (tried ? p[k] : undefined)
  const today = istDateKey()
  const licenceOk = hall
    ? Boolean(hall.fssaiLicenceNo && hall.fssaiLicenceValidTo && hall.fssaiLicenceValidTo >= today)
    : null
  const tol = scale?.checkToleranceG ?? DEFAULT_TOLERANCE_G
  const off = scaleOff(d.referenceG, d.readingG, tol)
  const scaleDue = scale
    ? [
        !scale.calibrationDueAt || scale.calibrationDueAt < today ? 'calibration overdue' : null,
        scale.stampDueAt && scale.stampDueAt < today ? 'stamp overdue' : null,
      ].filter(Boolean)
    : []
  const canUpload = can('uploads:create')

  const onPhoto = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      set({ cleanoutPhotoId: await uploadEvidence(file) })
    } catch (e) {
      toast.error('Photo not uploaded', errorMessage(e))
    } finally {
      setUploading(false)
    }
  }

  const submit = async () => {
    setTried(true)
    if (!startOk(p)) return
    try {
      setStarted(await start.mutateAsync({ id: run.id, body: startBody(d) }))
    } catch {
      /* shown in the dialog */
    }
  }

  if (started) return <StartedPanel run={run} started={started} onClose={onClose} />

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!start.isPending && !uploading}
      title={`Start ${run.code}`}
      description="Usually done on the pack-hall phone. Start here only at the hall desk, with the line clean and the label roll loaded. The lot code and dates are fixed when you start."
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose} disabled={start.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={start.isPending}
            disabled={uploading || licenceOk === false}
          >
            Start run
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {start.isError && <RefusalBanner error={start.error} title="The run did not start" />}
        {hall &&
          (licenceOk ? (
            <p className="text-[13.5px] text-muted">
              {hall.name} · FSSAI licence <Code>{hall.fssaiLicenceNo}</Code> valid to{' '}
              <When at={hall.fssaiLicenceValidTo} dateOnly />
            </p>
          ) : (
            <Banner tone="error" title="The hall's FSSAI licence is missing or expired">
              The run cannot start until it is renewed on the hall under Locations.
            </Banner>
          ))}

        <fieldset className="flex flex-col gap-3 rounded-card border border-rule p-3">
          <legend className="px-1 text-[14px] font-semibold">Cleanout</legend>
          <label className="flex items-start gap-2 text-[14px]">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-amber"
              checked={d.cleanoutDone}
              onChange={(e) => set({ cleanoutDone: e.target.checked })}
            />
            <span>The line and hopper are empty and clean.</span>
          </label>
          {shown('cleanoutDone') && (
            <p className="text-[13px] font-medium text-danger">{p.cleanoutDone}</p>
          )}
          <label className="flex items-start gap-2 text-[14px]">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-amber"
              checked={d.allergenCleanConfirmed}
              onChange={(e) => set({ allergenCleanConfirmed: e.target.checked })}
            />
            <span>
              Full allergen clean done.
              <span className="block text-[12.5px] text-muted">
                Needed when the last run on this line was another crop.
              </span>
            </span>
          </label>
          <Field label="Photo of the empty hopper" required error={shown('cleanoutPhotoId')}>
            {canUpload ? (
              <div className="flex flex-wrap items-center gap-3">
                {d.cleanoutPhotoId && (
                  <PhotoViewer photo={d.cleanoutPhotoId} alt="Empty hopper" size="sm" />
                )}
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-chip border border-rule bg-surface px-3 py-2 text-[14px] hover:border-ink/40">
                  {uploading ? <Spinner /> : <Camera aria-hidden="true" />}
                  {d.cleanoutPhotoId ? 'Take another photo' : 'Add photo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="environment"
                    className="sr-only"
                    disabled={uploading}
                    onChange={(e) => void onPhoto(e.target.files?.[0])}
                  />
                </label>
              </div>
            ) : (
              <p className="text-[13.5px] text-warn">
                You may not add photos here. Start this run on the pack-hall phone.
              </p>
            )}
          </Field>
          <Field
            label="Left in the line from the last run"
            required
            error={shown('leftoverKg')}
            hint="Type 0 if none. Anything above 0 is reported against the last run and never packed."
          >
            <Input
              inputMode="decimal"
              value={d.leftoverKg}
              onChange={(e) => set({ leftoverKg: e.target.value.replace(/[^\d.,]/g, '') })}
              trailing="kg"
              className="font-mono"
            />
          </Field>
        </fieldset>

        <fieldset className="flex flex-col gap-3 rounded-card border border-rule p-3">
          <legend className="px-1 text-[14px] font-semibold">Scale check</legend>
          <Field
            label="Scale"
            required
            error={shown('scaleId')}
            hint={
              scale ? (
                scaleDue.length ? (
                  <span className="text-danger">Blocked: {scaleDue.join(', ')}</span>
                ) : (
                  `Calibrated until ${scale.calibrationDueAt}${scale.stampDueAt ? ` · stamp until ${scale.stampDueAt}` : ''}`
                )
              ) : undefined
            }
          >
            <Select
              value={d.scaleId}
              onChange={(scaleId) => set({ scaleId })}
              placeholder={
                equipment.isLoading
                  ? 'Loading…'
                  : scales.length
                    ? 'Pick the scale'
                    : 'No scale registered at this hall'
              }
              options={scales.map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }))}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Check weight" required error={shown('referenceG')}>
              <Input
                inputMode="decimal"
                value={d.referenceG}
                onChange={(e) => set({ referenceG: e.target.value.replace(/[^\d.]/g, '') })}
                trailing="g"
                className="font-mono"
              />
            </Field>
            <Field
              label="Scale shows"
              required
              error={
                shown('readingG') ??
                (off ? `More than ${tol} g off — the run will be refused` : undefined)
              }
              hint={off === false ? `Within ${tol} g` : undefined}
            >
              <Input
                inputMode="decimal"
                value={d.readingG}
                onChange={(e) => set({ readingG: e.target.value.replace(/[^\d.]/g, '') })}
                trailing="g"
                className="font-mono"
              />
            </Field>
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-3 rounded-card border border-rule p-3">
          <legend className="px-1 text-[14px] font-semibold">Label roll</legend>
          <Field label="Label version on the roll" required error={shown('labelVersionId')}>
            <Select
              value={d.labelVersionId}
              onChange={(labelVersionId) => set({ labelVersionId, labelChecked: false })}
              placeholder={
                labels.isLoading
                  ? 'Loading…'
                  : labels.data?.length
                    ? 'Pick the version'
                    : 'No approved label at this hall'
              }
              options={(labels.data ?? []).map((l) => ({ value: l.id, label: l.code }))}
            />
          </Field>
          {label && (
            <>
              <Facts
                columns={2}
                items={[
                  { label: 'MRP', value: <Money paise={label.mrpPaise} /> },
                  { label: 'FSSAI licence', value: label.fssaiLicenceNo ?? '—', mono: true },
                  { label: 'Allergen line', value: label.allergenStatement ?? '—' },
                  { label: 'Claim', value: label.claimText ?? '—' },
                ]}
              />
              <label className="flex items-start gap-2 text-[14px]">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-amber"
                  checked={d.labelChecked}
                  onChange={(e) => set({ labelChecked: e.target.checked })}
                />
                <span>The roll matches these lines, including the allergen line.</span>
              </label>
              {shown('labelChecked') && (
                <p className="text-[13px] font-medium text-danger">{p.labelChecked}</p>
              )}
            </>
          )}
        </fieldset>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}

function StartedPanel({
  run,
  started,
  onClose,
}: {
  run: PackRunRow
  started: StartedRun
  onClose: () => void
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const coder = async () => {
    setBusy(true)
    try {
      await openFile(`/pack-lots/${started.packLotId}/coder-sheet.pdf`, `coder-${started.code}.pdf`)
    } catch (e) {
      toast.error('Coder sheet not opened', refusalOf(e).title)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={`${run.code} started`}
      description="Key these into the coder exactly as shown, or print the coder sheet and key from it. They cannot change now."
      footer={
        <>
          <Button
            variant="secondary"
            leadingIcon={<FilePdf />}
            loading={busy}
            onClick={() => void coder()}
          >
            Coder sheet
          </Button>
          <Button className="ml-auto" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Facts
          columns={2}
          items={[
            {
              label: 'Pack lot',
              value: <RefLink to={`/c/pack-lots/${started.packLotId}`}>{started.code}</RefLink>,
            },
            { label: 'Label version', value: started.labelVersionCode ?? '—', mono: true },
            { label: 'Pack date', value: started.packDate ?? '—', mono: true },
            { label: 'Best before', value: started.bestBefore ?? '—', mono: true },
          ]}
        />
        {started.qrUrl && (
          <div>
            <div className="font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
              QR link
            </div>
            <Code className="break-all">{started.qrUrl}</Code>
          </div>
        )}
        {started.leftoverExceptionId && (
          <Banner tone="warn" title="Leftover reported against the last run">
            <RefLink to={`/c/exceptions?open=${started.leftoverExceptionId}`} className="font-sans">
              Open the exception
            </RefLink>
          </Banner>
        )}
      </div>
    </Modal>
  )
}
